/**
 * FX (foreign exchange) helper.
 *
 * Fetches once per session, caches in sessionStorage. Provides a helper to
 * render compact "≈ £16/mo · ≈ $20/mo" approximations alongside the
 * headline plan price. Falls back to a conservative hardcoded set of rates
 * if the API is unreachable (e.g. behind a strict firewall) so the UI never
 * crashes.
 */
const STORAGE_KEY = "hac_fx_rates_v1";
const SUPPORTED = ["EUR", "USD", "GBP"];
const FALLBACK = { EUR: 1.0, USD: 1.08, GBP: 0.85 };

let _inflight = null;

const readCache = () => {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed && parsed.rates && SUPPORTED.every((c) => typeof parsed.rates[c] === "number")) {
      return parsed.rates;
    }
  } catch { /* ignore */ }
  return null;
};

const writeCache = (rates) => {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ rates, ts: Date.now() }));
  } catch { /* ignore */ }
};

/** Fetch live rates (or cached). Singleton — multiple parallel callers share
 * the same in-flight Promise so we never duplicate the network request. */
export const fetchRates = async () => {
  const cached = readCache();
  if (cached) return cached;
  if (_inflight) return _inflight;
  _inflight = (async () => {
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/fx/rates`, {
        headers: { Accept: "application/json" },
      });
      if (!res.ok) throw new Error(`fx ${res.status}`);
      const data = await res.json();
      const rates = data?.rates && SUPPORTED.every((c) => typeof data.rates[c] === "number")
        ? data.rates
        : FALLBACK;
      writeCache(rates);
      return rates;
    } catch {
      return FALLBACK;
    } finally {
      _inflight = null;
    }
  })();
  return _inflight;
};

/** Synchronous read — useful for components that have already kicked off the
 * fetch via useFxRates(). Returns null until rates are loaded. */
export const peekRates = () => readCache();

/** EUR-base conversion. */
export const convert = (amount, fromCcy, toCcy, rates) => {
  if (!rates || !amount) return amount;
  const f = rates[fromCcy] || rates[(fromCcy || "").toUpperCase()];
  const t = rates[toCcy] || rates[(toCcy || "").toUpperCase()];
  if (!f || !t || fromCcy === toCcy) return amount;
  return amount * (t / f);
};

const SYMBOLS = { EUR: "€", USD: "$", GBP: "£" };

/** Round + format a converted amount cleanly:
 *   23.4  → "23"
 *   23.49 → "23"
 *   2.78  → "2.8"  (small amounts keep 1 decimal)
 *   0.12  → "0.12" */
const fmtAmount = (v) => {
  const n = Math.abs(v);
  if (n >= 10) return Math.round(v).toString();
  if (n >= 1) return v.toFixed(1).replace(/\.0$/, "");
  return v.toFixed(2);
};

/** Return an array of compact approximation strings for a plan, e.g.
 *   ["≈ £16/mo", "≈ $20/mo"]
 * Skips the plan's own currency. Returns empty array for free plans or
 * plans whose monthly base is 0. */
export const planApproximations = (plan, rates, lang = "en") => {
  if (!plan || !rates) return [];
  const model = plan.model || (Number(plan.monthly) > 0 || Number(plan.yearly) > 0 ? "flat" : "free");
  if (model === "free") return [];
  const fromCcy = (plan.currency || "EUR").toUpperCase();
  const monthly = Number(plan.monthly) || 0;
  const unitMonthly = Number(plan.unit_price_monthly) || 0;
  // Pick a single representative amount to convert:
  // - flat       → monthly
  // - per_unit   → base + 1 unit
  const amount = model === "per_unit" ? monthly + unitMonthly : monthly;
  if (amount <= 0) return [];
  const moSuffix = lang === "nl" ? "mnd" : "mo";
  const others = SUPPORTED.filter((c) => c !== fromCcy);
  return others
    .map((to) => {
      const converted = convert(amount, fromCcy, to, rates);
      if (!converted || !isFinite(converted)) return null;
      const sym = SYMBOLS[to] || "";
      return `≈ ${sym}${fmtAmount(converted)}/${moSuffix}`;
    })
    .filter(Boolean);
};
